"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.createOptimisticMessage = createOptimisticMessage;
exports.normalizeConversation = normalizeConversation;
exports.normalizeMessage = normalizeMessage;
// Converts raw server/socket payloads (today's field names, e.g. `flaggedlanguage`,
// `_id: null` optimistic sentinel) into the stable internal Message/Conversation
// shape described in ./types.js, so the reducer/hooks never depend on
// channel-specific field naming.

function normalizeMessage(raw) {
  var _raw$_id, _raw$tempId, _ref2, _ref3, _raw$contactid, _raw$sendername, _raw$senderimage, _raw$description, _raw$uploadedchatfile, _raw$uploads, _raw$createdAt, _ref4, _raw$updatedAt, _raw$flaggeddescripti, _raw$component, _raw$componenttitle, _raw$componentid, _raw$parent, _raw$parentchatmessag, _raw$parentchatsender, _raw$reactions, _raw$starredby, _raw$status;
  let _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
    currentUserId = _ref.currentUserId;
  if (!raw) return null;
  const senderId = raw.sender != null ? raw.sender.toString() : null;
  return {
    id: (_raw$_id = raw._id) !== null && _raw$_id !== void 0 ? _raw$_id : null,
    tempId: (_raw$tempId = raw.tempId) !== null && _raw$tempId !== void 0 ? _raw$tempId : null,
    conversationId: (_ref2 = (_ref3 = (_raw$contactid = raw.contactid) !== null && _raw$contactid !== void 0 ? _raw$contactid : raw.groupid) !== null && _ref3 !== void 0 ? _ref3 : raw.taskid) !== null && _ref2 !== void 0 ? _ref2 : null,
    senderId,
    receiverId: raw.receiver != null ? raw.receiver.toString() : null,
    senderLabel: (_raw$sendername = raw.sendername) !== null && _raw$sendername !== void 0 ? _raw$sendername : "",
    senderImage: (_raw$senderimage = raw.senderimage) !== null && _raw$senderimage !== void 0 ? _raw$senderimage : "",
    body: (_raw$description = raw.description) !== null && _raw$description !== void 0 ? _raw$description : "",
    attachments: (_raw$uploadedchatfile = raw.uploadedchatfiles) !== null && _raw$uploadedchatfile !== void 0 ? _raw$uploadedchatfile : [],
    uploadIds: (_raw$uploads = raw.uploads) !== null && _raw$uploads !== void 0 ? _raw$uploads : [],
    createdAt: (_raw$createdAt = raw.createdAt) !== null && _raw$createdAt !== void 0 ? _raw$createdAt : null,
    updatedAt: (_ref4 = (_raw$updatedAt = raw.updatedAt) !== null && _raw$updatedAt !== void 0 ? _raw$updatedAt : raw.createdAt) !== null && _ref4 !== void 0 ? _ref4 : null,
    own: currentUserId != null && senderId != null ? senderId === currentUserId.toString() : false,
    requireResponse: !!raw.requireresponse,
    translation: raw.flaggedlanguage ? {
      language: raw.flaggedlanguage,
      body: (_raw$flaggeddescripti = raw.flaggeddescription) !== null && _raw$flaggeddescripti !== void 0 ? _raw$flaggeddescripti : ""
    } : null,
    component: (_raw$component = raw.component) !== null && _raw$component !== void 0 ? _raw$component : "",
    componentTitle: (_raw$componenttitle = raw.componenttitle) !== null && _raw$componenttitle !== void 0 ? _raw$componenttitle : "",
    componentId: (_raw$componentid = raw.componentid) !== null && _raw$componentid !== void 0 ? _raw$componentid : null,
    parent: (_raw$parent = raw.parent) !== null && _raw$parent !== void 0 ? _raw$parent : null,
    parentMessage: (_raw$parentchatmessag = raw.parentchatmessage) !== null && _raw$parentchatmessag !== void 0 ? _raw$parentchatmessag : "",
    parentSender: (_raw$parentchatsender = raw.parentchatsender) !== null && _raw$parentchatsender !== void 0 ? _raw$parentchatsender : "",
    reactions: ((_raw$reactions = raw.reactions) !== null && _raw$reactions !== void 0 ? _raw$reactions : []).map(reaction => {
      var _reaction$emoji;
      return {
        senderId: reaction.sender != null ? reaction.sender.toString() : null,
        emoji: (_reaction$emoji = reaction.emoji) !== null && _reaction$emoji !== void 0 ? _reaction$emoji : ""
      };
    }),
    starredByMe: currentUserId != null ? ((_raw$starredby = raw.starredby) !== null && _raw$starredby !== void 0 ? _raw$starredby : []).some(id => id != null && id.toString() === currentUserId.toString()) : false,
    status: (_raw$status = raw.status) !== null && _raw$status !== void 0 ? _raw$status : "sent",
    raw
  };
}

// Builds a client-side placeholder message shown immediately on send, before
// the server confirms. `tempId` is what later reconciles this into the
// confirmed message via messageHelpers.reconcileOptimisticMessage.
function createOptimisticMessage() {
  let _ref5 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    tempId = _ref5.tempId,
    _ref5$conversationId = _ref5.conversationId,
    conversationId = _ref5$conversationId === void 0 ? null : _ref5$conversationId,
    _ref5$senderId = _ref5.senderId,
    senderId = _ref5$senderId === void 0 ? null : _ref5$senderId,
    _ref5$senderLabel = _ref5.senderLabel,
    senderLabel = _ref5$senderLabel === void 0 ? "" : _ref5$senderLabel,
    _ref5$senderImage = _ref5.senderImage,
    senderImage = _ref5$senderImage === void 0 ? "" : _ref5$senderImage,
    _ref5$body = _ref5.body,
    body = _ref5$body === void 0 ? "" : _ref5$body,
    _ref5$attachments = _ref5.attachments,
    attachments = _ref5$attachments === void 0 ? [] : _ref5$attachments,
    _ref5$parent = _ref5.parent,
    parent = _ref5$parent === void 0 ? null : _ref5$parent,
    _ref5$parentMessage = _ref5.parentMessage,
    parentMessage = _ref5$parentMessage === void 0 ? "" : _ref5$parentMessage,
    _ref5$parentSender = _ref5.parentSender,
    parentSender = _ref5$parentSender === void 0 ? "" : _ref5$parentSender,
    _ref5$reactions = _ref5.reactions,
    reactions = _ref5$reactions === void 0 ? [] : _ref5$reactions,
    _ref5$starredByMe = _ref5.starredByMe,
    starredByMe = _ref5$starredByMe === void 0 ? false : _ref5$starredByMe,
    _ref5$translation = _ref5.translation,
    translation = _ref5$translation === void 0 ? null : _ref5$translation,
    _ref5$component = _ref5.component,
    component = _ref5$component === void 0 ? "" : _ref5$component,
    _ref5$componentTitle = _ref5.componentTitle,
    componentTitle = _ref5$componentTitle === void 0 ? "" : _ref5$componentTitle,
    _ref5$componentId = _ref5.componentId,
    componentId = _ref5$componentId === void 0 ? null : _ref5$componentId,
    createdAt = _ref5.createdAt;
  const timestamp = createdAt !== null && createdAt !== void 0 ? createdAt : new Date().toISOString();
  return {
    id: null,
    tempId,
    conversationId,
    senderId,
    receiverId: null,
    senderLabel,
    senderImage,
    body,
    attachments,
    uploadIds: [],
    createdAt: timestamp,
    updatedAt: timestamp,
    own: true,
    requireResponse: false,
    translation,
    component,
    componentTitle,
    componentId,
    parent,
    parentMessage,
    parentSender,
    reactions,
    starredByMe,
    status: "sending",
    raw: null
  };
}

// Fills in defaults for the object passed to selectConversation(), so callers
// only need to supply the fields relevant to the conversation kind being
// selected (a "task" selection doesn't need group fields, etc.).
function normalizeConversation() {
  var _input$id, _input$kind, _input$name, _input$logo, _input$email, _input$roomId, _input$phone, _input$address, _input$company, _input$componentSlug, _input$componentTitle, _input$componentId, _input$groupModerator, _input$groupModerator2, _input$groupModerator3, _input$groupMembers, _input$groupFiles, _input$groupMemberStr;
  let input = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return {
    id: (_input$id = input.id) !== null && _input$id !== void 0 ? _input$id : null,
    kind: (_input$kind = input.kind) !== null && _input$kind !== void 0 ? _input$kind : "contact",
    name: (_input$name = input.name) !== null && _input$name !== void 0 ? _input$name : "",
    logo: (_input$logo = input.logo) !== null && _input$logo !== void 0 ? _input$logo : "",
    email: (_input$email = input.email) !== null && _input$email !== void 0 ? _input$email : "",
    roomId: (_input$roomId = input.roomId) !== null && _input$roomId !== void 0 ? _input$roomId : null,
    phone: (_input$phone = input.phone) !== null && _input$phone !== void 0 ? _input$phone : "",
    address: (_input$address = input.address) !== null && _input$address !== void 0 ? _input$address : "",
    company: (_input$company = input.company) !== null && _input$company !== void 0 ? _input$company : "",
    component: {
      slug: (_input$componentSlug = input.componentSlug) !== null && _input$componentSlug !== void 0 ? _input$componentSlug : "",
      title: (_input$componentTitle = input.componentTitle) !== null && _input$componentTitle !== void 0 ? _input$componentTitle : "",
      id: (_input$componentId = input.componentId) !== null && _input$componentId !== void 0 ? _input$componentId : null
    },
    group: {
      moderatorName: (_input$groupModerator = input.groupModeratorName) !== null && _input$groupModerator !== void 0 ? _input$groupModerator : "",
      moderatorImage: (_input$groupModerator2 = input.groupModeratorImage) !== null && _input$groupModerator2 !== void 0 ? _input$groupModerator2 : "",
      moderatorId: (_input$groupModerator3 = input.groupModeratorId) !== null && _input$groupModerator3 !== void 0 ? _input$groupModerator3 : null,
      members: (_input$groupMembers = input.groupMembers) !== null && _input$groupMembers !== void 0 ? _input$groupMembers : [],
      files: (_input$groupFiles = input.groupFiles) !== null && _input$groupFiles !== void 0 ? _input$groupFiles : [],
      memberString: (_input$groupMemberStr = input.groupMemberString) !== null && _input$groupMemberStr !== void 0 ? _input$groupMemberStr : ""
    }
  };
}